/**
 * Live conversation interface.
 *
 * Renders the AI-led, time-boxed spoken dialogue for a Speaking conversation task
 * (the live-conversation capability): question + note section, a thinking
 * countdown, live speaker-attributed turns, talking indicators, and an end
 * control. All state/transport lives in {@link useLiveConversationState}; this
 * component is presentation only and reuses the design system (font-ui + CSS
 * variable colors + shadcn primitives).
 *
 * @module LiveConversationInterface
 */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Mic, PhoneOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useAuth } from '@/hooks/useAuth';
import { formatTime } from './SpeakingInterface.helpers';
import {
  useLiveConversationState,
  type LiveConversationMode,
} from '@/hooks/useLiveConversationState';
import type { SpeakingQuestion } from '@/types/exam';

interface LiveConversationInterfaceProps {
  readonly question: SpeakingQuestion;
  readonly sessionId: string | null;
  readonly level?: string | null;
  readonly examType?: string | null;
  readonly voiceConsentDenied?: boolean;
  /** Thinking window in seconds (LCT-002, default 90). */
  readonly thinkingSeconds?: number;
  /** Hard cap in seconds (LCT-004, default 240). */
  readonly maxSeconds?: number;
  readonly onFinish?: () => void;
}

function modeBadgeClass(mode: LiveConversationMode): string {
  const base =
    'absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-[10px] sm:text-sm font-semibold font-ui whitespace-nowrap';
  switch (mode) {
    case 'thinking':
      return `${base} bg-exam-prep text-white`;
    case 'connecting':
    case 'finalizing':
      return `${base} bg-exam-processing text-white`;
    case 'active':
      return `${base} bg-exam-recording text-white animate-pulse`;
    case 'done':
      return `${base} bg-exam-success text-white`;
    case 'error':
      return `${base} bg-destructive text-white`;
    default:
      return base;
  }
}

function modeBorderClass(mode: LiveConversationMode): string {
  switch (mode) {
    case 'thinking':
      return 'border-exam-prep';
    case 'connecting':
    case 'finalizing':
      return 'border-exam-processing';
    case 'active':
      return 'border-exam-recording';
    case 'done':
      return 'border-exam-success';
    case 'error':
      return 'border-destructive';
    default:
      return 'border-border/60';
  }
}

export function LiveConversationInterface({
  question,
  sessionId,
  level,
  examType,
  voiceConsentDenied = false,
  thinkingSeconds = 90,
  maxSeconds = 240,
  onFinish,
}: LiveConversationInterfaceProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  // The generated transcript is an authoring/QA aid — admins can see the live
  // turns; students only hear the AI voice and respond (LCT: no transcript for
  // normal users).
  const isAdmin = user?.userType === 'admin';
  const [candidateNotes, setCandidateNotes] = useState('');

  const {
    mode,
    thinkingTimeLeft,
    conversationTimeLeft,
    turns,
    aiSpeaking,
    userSpeaking,
    error,
    startNow,
    endConversation,
  } = useLiveConversationState({
    sessionId,
    questionId: question.id,
    questionText: question.prompt,
    notes: question.contextText ?? null,
    module: 'speaking',
    section: question.section ?? null,
    level: level ?? null,
    examType: examType ?? null,
    thinkingSeconds,
    maxSeconds,
    disabled: voiceConsentDenied,
    onFinalized: onFinish,
  });

  const modeLabel = useMemo(() => {
    switch (mode) {
      case 'thinking':
        return t('simulation.conversation.modes.thinking', { defaultValue: 'Prepare your ideas' });
      case 'connecting':
        return t('simulation.conversation.modes.connecting', { defaultValue: 'Connecting…' });
      case 'active':
        return t('simulation.conversation.modes.active', { defaultValue: 'Live Conversation' });
      case 'finalizing':
        return t('simulation.conversation.modes.finalizing', { defaultValue: 'Finishing up…' });
      case 'done':
        return t('simulation.conversation.modes.done', { defaultValue: 'Conversation Complete' });
      case 'error':
        return t('simulation.conversation.modes.error', { defaultValue: 'Connection Problem' });
      default:
        return '';
    }
  }, [mode, t]);

  return (
    <div className="flex h-[100dvh] items-center justify-center bg-muted/50 p-2 sm:p-4">
      <div className="absolute inset-0 bg-background/60 backdrop-blur-sm" />

      <div
        className={cn(
          'relative z-10 flex max-h-[calc(100dvh-1rem)] w-full max-w-2xl flex-col rounded-2xl border-4 bg-card shadow-2xl transition-all duration-500 sm:max-h-[calc(100dvh-2rem)]',
          modeBorderClass(mode),
        )}
      >
        <div className={modeBadgeClass(mode)}>{modeLabel}</div>

        <div className="custom-scrollbar min-h-0 overflow-y-auto p-3 pt-8 sm:p-8 sm:pt-10">
          {/* Question + note section (LCT-002) */}
          <div className="mb-4 sm:mb-6">
            <p className="font-reading text-base sm:text-lg text-foreground leading-relaxed whitespace-pre-line">
              {question.prompt}
            </p>
            {question.contextText && (
              <div className="mt-4 rounded-lg border border-border bg-muted/30 p-3 sm:p-4 text-xs sm:text-sm text-foreground shadow-sm">
                <p className="font-semibold text-muted-foreground mb-1.5 font-ui">
                  {t('simulation.conversation.notes', { defaultValue: 'Notes' })}
                </p>
                <p className="whitespace-pre-line leading-relaxed">{question.contextText}</p>
              </div>
            )}
          </div>

          {/* Candidate scratchpad (LCT-002). Editable while thinking, read-only once
              the conversation starts so the candidate can reference their notes. */}
          <div className="mb-6 rounded-lg border border-border bg-muted/30 p-3 sm:p-4">
            <label
              htmlFor="candidate-notes"
              className="font-semibold text-muted-foreground mb-1.5 block font-ui text-xs sm:text-sm"
            >
              {t('simulation.conversation.yourNotes', { defaultValue: 'Your notes' })}
            </label>
            <textarea
              id="candidate-notes"
              value={candidateNotes}
              onChange={(e) => setCandidateNotes(e.target.value)}
              placeholder={t('simulation.conversation.notesPlaceholder', {
                defaultValue: 'Jot down ideas you want to bring up in the conversation…',
              })}
              rows={4}
              readOnly={mode !== 'thinking'}
              className="w-full resize-none rounded-md border border-border bg-background px-3 py-2 font-reading text-sm text-foreground shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-70"
            />
          </div>

          {/* Thinking countdown */}
          {mode === 'thinking' && (
            <div className="mb-6 text-center">
              <div className="text-[10px] sm:text-xs text-muted-foreground uppercase tracking-wider mb-1 font-ui">
                {t('simulation.conversation.thinkingTimer', { defaultValue: 'Time to think' })}
              </div>
              <div
                role="timer"
                aria-live="polite"
                className="text-3xl sm:text-5xl font-bold tabular-nums font-ui text-exam-prep"
              >
                {formatTime(thinkingTimeLeft)}
              </div>
            </div>
          )}

          {/* Conversation timer (active/connecting only) + always-visible talking indicators */}
          <div className="mb-4 flex flex-col items-center gap-3">
            {(mode === 'active' || mode === 'connecting') && (
              <div
                role="timer"
                aria-live="polite"
                className="text-2xl sm:text-4xl font-bold tabular-nums font-ui text-exam-recording"
              >
                {formatTime(conversationTimeLeft)}
              </div>
            )}
            {/* Whose turn it is. The active speaker's chip fills with its color,
                scales up, gets a ring + pulsing dot and a "speaking…" status; the
                idle side is dimmed and shrunk — so it's unmistakable who is talking. */}
            <div className="flex items-center justify-center gap-3 sm:gap-4">
              <div
                aria-live="polite"
                className={cn(
                  'flex items-center gap-2 rounded-full font-ui font-semibold px-4 py-2 text-sm sm:text-base transition-all duration-200',
                  aiSpeaking
                    ? 'bg-exam-prep text-white scale-105 shadow-lg ring-2 ring-exam-prep/40'
                    : 'bg-muted text-muted-foreground/50 scale-95',
                )}
              >
                <span
                  className={cn(
                    'h-2.5 w-2.5 rounded-full',
                    aiSpeaking ? 'bg-white animate-pulse' : 'bg-muted-foreground/30',
                  )}
                />
                {t('simulation.conversation.aiSpeaking', { defaultValue: 'Partner' })}
                {aiSpeaking && (
                  <span className="text-xs font-normal opacity-90">
                    {t('simulation.conversation.speakingNow', { defaultValue: 'speaking…' })}
                  </span>
                )}
              </div>
              <div
                aria-live="polite"
                className={cn(
                  'flex items-center gap-2 rounded-full font-ui font-semibold px-4 py-2 text-sm sm:text-base transition-all duration-200',
                  userSpeaking
                    ? 'bg-exam-recording text-white scale-105 shadow-lg ring-2 ring-exam-recording/40'
                    : 'bg-muted text-muted-foreground/50 scale-95',
                )}
              >
                <Mic className="h-4 w-4" />
                {t('simulation.conversation.youSpeaking', { defaultValue: 'You' })}
                {userSpeaking && (
                  <span className="text-xs font-normal opacity-90">
                    {t('simulation.conversation.speakingNow', { defaultValue: 'speaking…' })}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Live transcript — ADMIN ONLY. Students must not see the generated
              transcript; they just hear the AI voice and respond. Admins keep it
              as an authoring/QA aid. The turns still stream + persist server-side
              regardless; this only gates the on-screen display. */}
          {isAdmin && (
            <div className="mb-6 space-y-2 min-h-[3rem]">
              {turns.length === 0 ? (
                <p className="text-center text-xs sm:text-sm text-muted-foreground/70 font-ui py-4">
                  {t('simulation.conversation.turnsPlaceholder', {
                    defaultValue: 'The conversation will appear here once it begins.',
                  })}
                </p>
              ) : (
                turns.map((turn, index) => (
                  <div
                    key={`${turn.role}-${index}`}
                    className={cn(
                      'rounded-lg p-2.5 sm:p-3 text-sm sm:text-base font-reading leading-relaxed',
                      turn.role === 'ai'
                        ? 'bg-exam-prep/10 text-foreground'
                        : 'bg-exam-recording/10 text-foreground',
                    )}
                  >
                    <span className="font-semibold font-ui mr-1.5 text-muted-foreground">
                      {turn.role === 'ai'
                        ? t('simulation.conversation.aiLabel', { defaultValue: 'AI' })
                        : t('simulation.conversation.userLabel', { defaultValue: 'You' })}
                      :
                    </span>
                    {turn.text}
                  </div>
                ))
              )}
            </div>
          )}

          {/* Errors */}
          {mode === 'error' && error && (
            <p className="mb-4 text-center text-sm text-destructive font-ui">{error}</p>
          )}

          {/* Action area */}
          <div className="flex flex-col items-center gap-3">
            {mode === 'thinking' && (
              <Button onClick={startNow} disabled={voiceConsentDenied} className="font-ui">
                {t('simulation.conversation.startNow', { defaultValue: 'Start conversation now' })}
              </Button>
            )}
            {(mode === 'connecting' || mode === 'finalizing') && (
              <div className="flex items-center gap-2 text-muted-foreground font-ui">
                <Loader2 className="h-4 w-4 animate-spin" />
                {modeLabel}
              </div>
            )}
            {mode === 'active' && (
              <Button variant="destructive" onClick={endConversation} className="font-ui gap-2">
                <PhoneOff className="h-4 w-4" />
                {t('simulation.conversation.end', { defaultValue: 'End conversation' })}
              </Button>
            )}
            {mode === 'done' && (
              <Button onClick={onFinish} className="font-ui">
                {t('simulation.conversation.continue', { defaultValue: 'Continue' })}
              </Button>
            )}
            {mode === 'error' && (
              <Button onClick={onFinish} variant="outline" className="font-ui">
                {t('common.continue', { defaultValue: 'Continue' })}
              </Button>
            )}
          </div>

          {voiceConsentDenied && mode === 'thinking' && (
            <p className="mt-4 text-center text-xs text-muted-foreground font-ui">
              {t('simulation.conversation.consentRequired', {
                defaultValue: 'Microphone access and voice consent are required to start.',
              })}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
